// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: speech controls for assistant replies. The header toggle turns automatic
// reading on and off; the per-reply row (AssistantReadAloud) is a quiet icon strip
// under each answer: copy, read aloud, and pause/stop once that reply is playing.

import {ChannelHeaderIcon} from '@app/features/channel/components/channel_header_components/ChannelHeaderIcon';
import styles from '@app/features/konvo_assistant/components/AssistantSpeechControls.module.css';
import AssistantSpeech from '@app/features/konvo_assistant/state/AssistantSpeech';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {CheckIcon, CopyIcon, PauseIcon, PlayIcon, SpeakerHighIcon, StopIcon} from '@app/features/ui/icons/Icons';
import {formatMessageForTts} from '@app/features/voice/utils/TtsTextFormatter';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const AUTO_ON = msg({message: 'Konvo will read new replies aloud.', comment: 'Toast after enabling auto read.'});
const AUTO_OFF = msg({message: 'Konvo will stop reading replies aloud.', comment: 'Toast after disabling auto read.'});

function toggleAutomatic(i18n: ReturnType<typeof useLingui>['i18n']): void {
	const next = !AssistantSpeech.automatic;
	AssistantSpeech.setAutomatic(next);
	ToastCommands.createToast({type: 'success', children: i18n._(next ? AUTO_ON : AUTO_OFF)});
}

export const AssistantSpeechToggle = observer(({compact = false}: {compact?: boolean}) => {
	const {i18n} = useLingui();
	const label = i18n._(msg`Read replies aloud`);
	return (
		<span className={styles.controls} data-flx="konvo-assistant.speech-controls">
			{compact ? (
				<ChannelHeaderIcon
					icon={SpeakerHighIcon}
					label={label}
					className={styles.headerButton}
					isSelected={AssistantSpeech.automatic}
					aria-pressed={AssistantSpeech.automatic}
					onClick={() => toggleAutomatic(i18n)}
					data-flx="konvo-assistant.auto-read"
				/>
			) : (
				<button
					type="button"
					className={styles.button}
					aria-label={label}
					title={label}
					aria-pressed={AssistantSpeech.automatic}
					onClick={() => toggleAutomatic(i18n)}
					data-flx="konvo-assistant.auto-read"
				>
					<SpeakerHighIcon size={18} />
					<span>{label}</span>
				</button>
			)}
			{AssistantSpeech.currentId &&
				(compact ? (
					<ChannelHeaderIcon
						icon={AssistantSpeech.paused ? PlayIcon : PauseIcon}
						label={AssistantSpeech.paused ? i18n._(msg`Resume reading`) : i18n._(msg`Pause reading`)}
						onClick={AssistantSpeech.togglePaused}
						data-flx="konvo-assistant.pause-reading"
					/>
				) : (
					<button
						type="button"
						className={styles.button}
						aria-label={AssistantSpeech.paused ? i18n._(msg`Resume reading`) : i18n._(msg`Pause reading`)}
						title={AssistantSpeech.paused ? i18n._(msg`Resume reading`) : i18n._(msg`Pause reading`)}
						onClick={AssistantSpeech.togglePaused}
						data-flx="konvo-assistant.pause-reading"
					>
						{AssistantSpeech.paused ? <PlayIcon size={18} /> : <PauseIcon size={18} />}
					</button>
				))}
			{AssistantSpeech.currentId &&
				(compact ? (
					<ChannelHeaderIcon
						icon={StopIcon}
						label={i18n._(msg`Stop reading`)}
						onClick={AssistantSpeech.stop}
						data-flx="konvo-assistant.stop-reading"
					/>
				) : (
					<button
						type="button"
						className={styles.button}
						aria-label={i18n._(msg`Stop reading`)}
						title={i18n._(msg`Stop reading`)}
						onClick={AssistantSpeech.stop}
						data-flx="konvo-assistant.stop-reading"
					>
						<StopIcon size={18} />
					</button>
				))}
			{AssistantSpeech.error && (
				<span role="status" className={styles.error}>
					<Trans>Could not play speech. Try Read aloud again.</Trans>
				</span>
			)}
		</span>
	);
});

export const AssistantReadAloud = observer(({id, text}: {id: string; text: string}) => {
	const {i18n} = useLingui();
	const [copied, setCopied] = useState(false);
	const copiedTimer = useRef<number | null>(null);
	useEffect(
		() => () => {
			if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
		},
		[],
	);
	if (!text.trim()) return null;
	const active = AssistantSpeech.currentId === id;
	const readLabel = active ? i18n._(msg`Stop reading`) : i18n._(msg`Read aloud`);
	const pauseLabel = AssistantSpeech.paused ? i18n._(msg`Resume`) : i18n._(msg`Pause`);
	const copyLabel = copied ? i18n._(msg`Copied`) : i18n._(msg`Copy reply`);
	const copy = () => {
		void navigator.clipboard?.writeText(text).then(() => {
			setCopied(true);
			if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
			copiedTimer.current = window.setTimeout(() => setCopied(false), 1500);
		});
	};
	return (
		<span className={styles.replyActionsHost} data-flx="konvo-assistant.reply-actions-host">
			<span className={styles.replyActions} data-flx="konvo-assistant.reply-actions">
				<button
					type="button"
					className={styles.iconButton}
					aria-label={copyLabel}
					title={copyLabel}
					onClick={copy}
					data-flx="konvo-assistant.copy-reply"
				>
					{copied ? <CheckIcon size={17} /> : <CopyIcon size={17} />}
				</button>
				<button
					type="button"
					className={styles.iconButton}
					aria-label={readLabel}
					title={readLabel}
					aria-pressed={active}
					onClick={() => AssistantSpeech.read(id, formatMessageForTts(text, '', null, i18n))}
					data-flx="konvo-assistant.read-aloud"
				>
					{active ? <StopIcon size={17} /> : <SpeakerHighIcon size={17} />}
				</button>
				{active && (
					<>
						<button
							type="button"
							className={styles.iconButton}
							aria-label={pauseLabel}
							title={pauseLabel}
							onClick={AssistantSpeech.togglePaused}
							data-flx="konvo-assistant.pause-reply"
						>
							{AssistantSpeech.paused ? <PlayIcon size={17} /> : <PauseIcon size={17} />}
						</button>
						<span className={clsx(styles.speaking, AssistantSpeech.paused && styles.speakingPaused)} role="status">
							<span className={styles.bars} aria-hidden="true">
								<i />
								<i />
								<i />
							</span>
							{AssistantSpeech.paused ? <Trans>Paused</Trans> : <Trans>Reading</Trans>}
						</span>
					</>
				)}
			</span>
		</span>
	);
});
